import { Group, TextInput, Button, Text } from '@mantine/core';
import { Search, X, Download } from 'lucide-react';
import { useState, useMemo } from 'react';
import { FilterPanel } from '../../common/FilterPanel/FilterPanel';
import type { QuickFilterConfig, FilterFieldConfig, ActiveFilterRow } from '../../common/FilterPanel/FilterPanel';
import { SortPanel } from '../../common/SortPanel/SortPanel';
import type { SortFieldConfig } from '../../common/SortPanel/SortPanel';
import { GroupPanel } from '../../common/GroupPanel/GroupPanel';
import { SavedViewsMenu } from '../components/SavedViewsMenu';
import type { GroupFieldConfig, GroupState } from '../../common/GroupPanel/GroupPanel';
import { deriveGroupFields } from '../../../utils/groupDataByField';
import { ColumnSettingsPopover } from '../../common/ColumnSettingsPopover/ColumnSettingsPopover';
import { ExportModal } from '../../common/ExportModal/ExportModal';
import { exportSummaryProps, splitScreenFilters } from '../../common/ExportModal/filterSummary';
import type { ColumnConfig } from '../../../hooks/useColumnConfig';
import type { SortState } from '../../../utils/sortTasks';
import type { SortConfig } from '../../../hooks/useColumnConfig';
import type { DataLibrarySurveyFilterOptions } from '../../../types';
import { clearThenFocusFilter } from '../../../utils/focusFilterTrigger';

interface SurveyTabToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  filterOptions: DataLibrarySurveyFilterOptions;
  activeQuickFilters: string[];
  onQuickFilterToggle: (key: string) => void;
  activeFilters: ActiveFilterRow[];
  onFilterAdd: (key: string) => void;
  onFilterChange: (key: string, values: string[]) => void;
  onFilterRemove: (key: string) => void;
  onClearAll: () => void;
  hasActiveFilters: boolean;
  sortConfig: SortConfig | null;
  onSortChange: (sort: SortState | null) => void;
  activeGroup: GroupState | null;
  /** Filtered row total when it is too large to group */
  groupLimitTotal?: number | null;
  onGroupChange: (group: GroupState | null) => void;
  columns: ColumnConfig[];
  onColumnsChange: (columns: ColumnConfig[]) => void;
  totalCount: number;
  entityLabel: string;
  hideGroupPanel?: boolean;
  isMidVsEnd?: boolean;
  selectedCohortIds: string[];
  /** MED-1234: participant survey form types (mid_program / final_survey) for export routing */
  formTypes: string[];
}

const QUICK_FILTERS: QuickFilterConfig[] = [
  { key: 'flagged', label: 'Flagged' },
  { key: 'absences', label: 'Has Absences' },
];

const SORT_FIELDS: SortFieldConfig[] = [
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'group', label: 'Group' },
  { value: 'coach', label: 'Coach' },
  { value: 'attendance', label: 'Attendance Rate' },
  { value: 'avg_engagement', label: 'Avg Engagement' },
];

/** Extra group field for mid-vs-end mode: groups by participant email with midpoint-first sorting */
const PARTICIPANT_GROUP_FIELD: GroupFieldConfig = { value: 'participant', label: 'Participant' };

export function SurveyTabToolbar({
  searchValue,
  onSearchChange,
  filterOptions,
  activeQuickFilters,
  onQuickFilterToggle,
  activeFilters,
  onFilterAdd,
  onFilterChange,
  onFilterRemove,
  onClearAll,
  hasActiveFilters,
  sortConfig,
  onSortChange,
  activeGroup,
  groupLimitTotal,
  onGroupChange,
  columns,
  onColumnsChange,
  totalCount,
  entityLabel,
  hideGroupPanel,
  isMidVsEnd,
  selectedCohortIds,
  formTypes,
}: SurveyTabToolbarProps) {
  const [exportOpen, setExportOpen] = useState(false);
  const groupFields = useMemo(() => {
    const derived = deriveGroupFields(columns);
    // Mid-vs-end mode adds a special "Participant" grouping (groups by email with midpoint-first sorting)
    return isMidVsEnd ? [PARTICIPANT_GROUP_FIELD, ...derived] : derived;
  }, [columns, isMidVsEnd]);
  const filterFields: FilterFieldConfig[] = useMemo(
    () => {
      const fields: FilterFieldConfig[] = [
        {
          key: 'group',
          label: 'Group',
          type: 'multiselect' as const,
          options: filterOptions.groups,
        },
        {
          key: 'coach',
          label: 'Coach',
          type: 'select' as const,
          options: filterOptions.coaches,
        },
        {
          key: 'overall_satisfaction',
          label: 'Overall Satisfaction',
          type: 'select' as const,
          options: [
            { value: 'Very satisfied', label: 'Very satisfied' },
            { value: 'Satisfied', label: 'Satisfied' },
            { value: 'Neutral', label: 'Neutral' },
            { value: 'Dissatisfied', label: 'Dissatisfied' },
            { value: 'Very dissatisfied', label: 'Very dissatisfied' },
          ],
        },
      ];
      if (isMidVsEnd) {
        fields.push({
          key: 'survey_stage',
          label: 'Survey Stage',
          type: 'select' as const,
          options: [
            { value: 'Midpoint', label: 'Midpoint' },
            { value: 'Endpoint', label: 'Endpoint' },
          ],
        });
      }
      return fields;
    },
    [filterOptions, isMidVsEnd],
  );

  const activeSortState: SortState | null = sortConfig
    ? { field: sortConfig.key, direction: sortConfig.order === 'ASC' ? 'asc' : 'desc' }
    : null;

  return (
    <Group justify="space-between" mb="sm" wrap="wrap" gap="xs">
      <Group gap="xs" wrap="wrap">
        <TextInput
          placeholder={`Search ${entityLabel}...`}
          size="sm"
          leftSection={<Search size={14} />}
          rightSection={
            searchValue ? (
              <X
                size={14}
                style={{ cursor: 'pointer' }}
                onClick={() => onSearchChange('')}
              />
            ) : null
          }
          value={searchValue}
          onChange={e => onSearchChange(e.currentTarget.value)}
          w={220}
        />

        <FilterPanel
          quickFilters={QUICK_FILTERS}
          activeQuickFilters={activeQuickFilters}
          onQuickFilterToggle={onQuickFilterToggle}
          filterFields={filterFields}
          activeFilters={activeFilters}
          onFilterAdd={onFilterAdd}
          onFilterChange={onFilterChange}
          onFilterRemove={onFilterRemove}
          onClearAll={onClearAll}
          size="sm"
        />

        <SortPanel
          sortFields={SORT_FIELDS}
          activeSort={activeSortState}
          defaultSort={{ field: 'first_name', direction: 'asc' }}
          onSortChange={onSortChange}
          size="sm"
        />

        {!hideGroupPanel && (
          <GroupPanel
            groupFields={groupFields}
            activeGroup={activeGroup}
            limitTotal={groupLimitTotal}
            onGroupChange={onGroupChange}
            size="sm"
          />
        )}

        <SavedViewsMenu
          columns={columns}
          onColumnsChange={onColumnsChange}
          filterFields={filterFields}
          activeFilters={activeFilters}
          onFilterAdd={onFilterAdd}
          onFilterChange={onFilterChange}
          onFilterRemove={onFilterRemove}
          quickFilters={QUICK_FILTERS}
          activeQuickFilters={activeQuickFilters}
          onQuickFilterToggle={onQuickFilterToggle}
          sortFields={SORT_FIELDS}
          activeSort={activeSortState}
          onSortChange={onSortChange}
          groupFields={groupFields}
          activeGroup={activeGroup}
          onGroupChange={onGroupChange}
          size="sm"
        />

        {hasActiveFilters && (
          <Button variant="subtle" size="sm" color="gray" leftSection={<X size={14} />} onClick={clearThenFocusFilter(onClearAll)}>
            Clear
          </Button>
        )}
      </Group>

      <Group gap="xs">
        <Text size="sm" c="dimmed">
          {totalCount} response{totalCount !== 1 ? 's' : ''}
        </Text>
        <ColumnSettingsPopover columns={columns} onColumnsChange={onColumnsChange} zIndex={1000} />
        <Button
          variant="outline"
          color="gray"
          bg="white"
          size="sm"
          style={{ borderColor: '#E8E6E3' }}
          leftSection={<Download size={14} />}
          onClick={() => setExportOpen(true)}
        >
          Export
        </Button>
      </Group>

      <ExportModal
        opened={exportOpen}
        onClose={() => setExportOpen(false)}
        entityType="survey_responses"
        contextType="global"
        scopeContext={{ cohortIds: selectedCohortIds, surveyKind: 'participant_survey', formTypes }}
        filterContext={{ groupIds: activeFilters.find(f => f.key === 'group')?.values }}
        {...exportSummaryProps(
          splitScreenFilters({ search: searchValue, quickFilters: QUICK_FILTERS, activeQuickFilters, fields: filterFields, rows: activeFilters }, ['group']),
        )}
        currentViewColumns={columns.filter(c => c.visible).map(c => c.id)}
        gridColumns={columns}
        recordCount={totalCount}
      />
    </Group>
  );
}
